import { subscribeToTicketMessages } from "entities/customerTickets/api/subscribeToTicketMessages";
import { Message } from "entities/customerTickets/types";
import { DocumentSnapshot, Unsubscribe } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { omit } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { TablePaginatedData, TablePaginationParams } from "shared/types/tables";

type UseTicketMessagesProps = {
  ticketId: string;
  initialPaginationParams: TablePaginationParams;
  reorderTickets: (ticketId: string) => void;
  readAllTicketMessages: (ticketId: string) => void;
};

type TicketMessages = {
  [key: string]: {
    items: Message[];
    totalCount: number;
    startListItem: DocumentSnapshot | null;
    endListItem: DocumentSnapshot | null;
  };
};

const mergeMessages = (prevItems: Message[], newItems: Message[]) => {
  const itemMap = new Map<string, Message>();
  prevItems.forEach((item) => itemMap.set(item.id, item));
  newItems.forEach((item) => itemMap.set(item.id, item));

  // Convert the map to an array and sort by timestamp
  return Array.from(itemMap.values()).sort((a, b) => {
    const timestampA = (a.timestamp as Timestamp).toMillis();
    const timestampB = (b.timestamp as Timestamp).toMillis();
    return timestampB - timestampA; // Sort in descending order
  });
};

export const useTicketMessagesList = ({
  ticketId,
  initialPaginationParams,
  reorderTickets,
  readAllTicketMessages,
}: UseTicketMessagesProps) => {
  const [data, setData] = useState<TicketMessages>({});
  const [paginationParams, setPaginationParams] =
    useState<TablePaginationParams>(initialPaginationParams);
  const [loading, setLoading] = useState<boolean>(false);

  const updateData = useCallback(
    (
      newData: TablePaginatedData<Message> & {
        ticketAdminUnreadMessagesCount: number;
      },
      ticketId: string,
    ) => {
      setData((prevData) => {
        const currentData = prevData[ticketId] || { items: [], totalCount: 0 };
        const currentItems = currentData.items;
        const currentTotalCount = currentData.totalCount;

        const updatedData = {
          ...omit(newData, newData.ticketAdminUnreadMessagesCount),
          items: mergeMessages(currentItems, newData.items),
        };

        if (currentItems.length > 0 && newData.totalCount > currentTotalCount) {
          reorderTickets(ticketId);
        }

        if (newData.ticketAdminUnreadMessagesCount > 0) {
          readAllTicketMessages(ticketId);
        }

        return {
          ...prevData,
          [ticketId]: updatedData,
        };
      });

      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!ticketId) {
      setLoading(false);
      return;
    }

    let unsubscribe: Unsubscribe | undefined;

    setLoading(true);

    try {
      unsubscribe = subscribeToTicketMessages(
        paginationParams,
        (
          newData: TablePaginatedData<Message> & {
            ticketAdminUnreadMessagesCount: number;
          },
        ) => {
          updateData(newData, ticketId);
        },
        ticketId,
      );
    } catch (err) {
      console.error(err);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [paginationParams, ticketId, updateData]);

  const loadNextPage = () => {
    if (ticketId && data[ticketId]?.endListItem) {
      setLoading(true);
      setPaginationParams((prevParams) => ({
        ...prevParams,
        endListItem: data[ticketId].endListItem,
      }));
    }
  };

  return {
    data: ticketId ? data[ticketId]?.items : [],
    totalMessages: ticketId ? data[ticketId]?.totalCount : 0,
    loading,
    fetchNextPage: loadNextPage,
  };
};
